import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import { useRouteLoaderData } from '@remix-run/react';
import type { loader } from '~/root.tsx';
import {
  CONFIRMATION_TOAST_EVENT,
  isConfirmationToastDetail,
  type ConfirmationToastDetail,
} from '~/utils/confirmation-toast.ts';
import { ConfirmationToast } from './confirmation-toast.tsx';
import { getMiniAudioPlayerBottomOffset } from './get-mini-audio-player-bottom-offset.ts';

const AUTO_DISMISS_MS = 1000;

export const DEFAULT_CLOSE_ICON =
  'data:image/svg+xml,%3Csvg%20width%3D%2220%22%20height%3D%2220%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M15%205L5%2015M5%205L15%2015%22%20stroke%3D%22%23EFEFEF%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E';

/**
 * Resolves the toast close icon URL, falling back when the theme value is missing or empty.
 */
export function getConfirmationToastCloseIconUrl(
  closeIcon: string | undefined | null
): string {
  return closeIcon || DEFAULT_CLOSE_ICON;
}

/**
 * Global host that renders confirmation toasts app-wide.
 */
export function ConfirmationToastHost(): JSX.Element | null {
  const rootData = useRouteLoaderData<typeof loader>('root');
  const closeIconUrl = getConfirmationToastCloseIconUrl(
    rootData?.confirmationToastCloseIcon
  );

  const [message, setMessage] = useState<string | null>(null);
  const [bottomOffsetPx, setBottomOffsetPx] = useState<number>(
    getMiniAudioPlayerBottomOffset()
  );
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearDismissTimer = useCallback((): void => {
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
  }, []);

  const dismiss = useCallback((): void => {
    clearDismissTimer();
    setMessage(null);
  }, [clearDismissTimer]);

  const show = useCallback(
    (detail: ConfirmationToastDetail): void => {
      clearDismissTimer();
      setMessage(detail.message);
      dismissTimerRef.current = setTimeout(() => {
        setMessage(null);
        dismissTimerRef.current = null;
      }, AUTO_DISMISS_MS);
    },
    [clearDismissTimer]
  );

  useEffect(() => {
    const handler = (event: Event): void => {
      const customEvent = event as CustomEvent<ConfirmationToastDetail>;
      if (!isConfirmationToastDetail(customEvent.detail)) {
        return;
      }

      show(customEvent.detail);
    };

    window.addEventListener(CONFIRMATION_TOAST_EVENT, handler);
    return () => {
      window.removeEventListener(CONFIRMATION_TOAST_EVENT, handler);
      clearDismissTimer();
    };
  }, [show, clearDismissTimer]);

  useEffect(() => {
    if (!message) {
      return;
    }

    const updateBottomOffset = (): void => {
      const nextOffsetPx = getMiniAudioPlayerBottomOffset();
      setBottomOffsetPx((currentOffsetPx) =>
        currentOffsetPx === nextOffsetPx ? currentOffsetPx : nextOffsetPx
      );
    };

    updateBottomOffset();
    window.addEventListener('resize', updateBottomOffset);

    const mutationObserver = new MutationObserver(updateBottomOffset);
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden'],
    });

    return () => {
      window.removeEventListener('resize', updateBottomOffset);
      mutationObserver.disconnect();
    };
  }, [message]);

  if (!message) return null;

  return (
    <ConfirmationToast
      message={message}
      closeIconUrl={closeIconUrl}
      onClose={dismiss}
      bottomOffsetPx={bottomOffsetPx}
    />
  );
}
